import { Calendar } from '@/components/calendar/Calendar';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useDeleteRosterMutation } from '@/services/gql/graphql.generated';
import { useIsOptimiserRunning } from '@/services/optimiser/useOptimiserJobs';
import { format } from 'date-fns';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RosterOption, useRefreshRosters } from '../providers/RosterOptionsService';
import strings from '../roster.translations';
import { RosterCalendarContent } from './RosterCalendarContent';
import { RosterCreateModal } from './RosterCreateModal';
import { useRosterCalendarEvents } from './useRosterCalendarEvents';

interface Props {
  date: Date;
}

export const RosterCalendar: FC<Props> = ({ date }) => {
  const navigate = useNavigate();

  const onSelect = useCallback(
    (option: RosterOption) => {
      navigate(`/rosters/${option.rosterDate}`);
    },
    [navigate]
  );

  const [dateToCreate, setDateToCreate] = useState<Date | undefined>(undefined);
  const [optionToDelete, setOptionToDelete] = useState<RosterOption | undefined>(undefined);

  const rosterEvents = useRosterCalendarEvents(date, setDateToCreate, setOptionToDelete, onSelect);
  const refreshRosters = useRefreshRosters();

  // Tracks whether the optimiser was running in the previous render
  const isOptimiserRunning = useIsOptimiserRunning(date);
  const wasOptimiserRunningRef = useRef(isOptimiserRunning);

  useEffect(() => {
    // Trigger refresh only when the optimiser transitions from running → not running
    // (i.e., optimisation has just completed)
    if (wasOptimiserRunningRef.current && !isOptimiserRunning) {
      refreshRosters();
    }

    // Update the ref to store the current state for the next render
    wasOptimiserRunningRef.current = isOptimiserRunning;
  }, [isOptimiserRunning, refreshRosters]);

  const [, deleteRoster] = useDeleteRosterMutation();
  const [deleteFetching, setDeleteFetching] = useState<boolean>(false);

  const onDelete = useCallback(async () => {
    if (!optionToDelete) {
      throw Error('optionToDelete should be specified.');
    }

    setDeleteFetching(true);

    const result = await deleteRoster({ rosterId: optionToDelete.id });

    await handleMutationResult(result, {
      successMessage: "Roster successfully deleted.",
      errorMessage: "Unable to delete roster.",
      onSuccess: async () => {
        await refreshRosters();
        setOptionToDelete(undefined);
      }
    });

    // Close the modal before clearing the loading state.
    setTimeout(() => setDeleteFetching(false), 100);
  }, [optionToDelete]);

  return (
    <>
      <Calendar monthToShow={date} events={rosterEvents} ContentComponent={RosterCalendarContent} />
      {dateToCreate && (
        <RosterCreateModal
          open={!!dateToCreate}
          date={dateToCreate}
          onClose={() => {
            setDateToCreate(undefined);
          }}
        />
      )}
      {optionToDelete && (
        <ConfirmDialog
          title={strings.deleteRosterConfirmModal.title}
          confirmLabel={strings.deleteRosterConfirmModal.confirm}
          cancelLabel={strings.common.cancel}
          open={!!optionToDelete}
          onConfirm={onDelete}
          onClose={() => setOptionToDelete(undefined)}
          loading={deleteFetching}
          dangerous
        >
          {strings.deleteRosterConfirmModal.message(format(new Date(optionToDelete.rosterDate), 'MMMM do'))}
        </ConfirmDialog>
      )}
    </>
  );
};
